import React, { useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import StepUpDialog from './StepUpDialog';
import { getVoidPreview, voidInvoice } from './api';
import { makeCommandId } from '../components/InvoiceApprovalReview';

/**
 * Void an approved invoice (backend/invoice_void.py). One screen says what will be taken back (prices, and stock by item)
 * and asks why; then the second check (StepUpDialog, action void_invoice) before it runs. Shown to owners and admins
 * for an invoice that is approved or posted automatically; the server decides who may and refuses the rest.
 */
export default function VoidInvoice({ invoice, onVoided }) {
  const [step, setStep] = useState(null);          // null | 'review' | 'confirm'
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const command = useRef(null);

  const open = async () => {
    setError(null); setReason(''); setPreview(null); setLoading(true); setStep('review');
    command.current = makeCommandId('inv-void');
    try { setPreview(await getVoidPreview(invoice.id)); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  const close = () => { setStep(null); setError(null); };

  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-5 space-y-2" data-testid="void-invoice">
      <h2 className="font-display text-lg text-ink">Wrong invoice?</h2>
      <p className="text-sm text-ink-muted">
        If there is no corrected version to replace it, void it. Its prices come off and the stock it added is taken
        back. The invoice stays on record as voided.
      </p>
      <button type="button" onClick={open} data-testid="void-invoice-btn"
        className="min-h-[44px] px-4 rounded-xl border border-danger text-danger hover:bg-surface-sunken">
        Void invoice
      </button>

      <ConfirmDialog open={step === 'review'} title={`Void invoice ${invoice.invoice_no || ''}?`} testid="void-dialog" danger
        confirmLabel="Continue" confirmDisabled={loading || !preview?.can_void || !reason.trim()}
        onCancel={close} onConfirm={() => setStep('confirm')}>
        {loading && <p data-testid="void-loading">Checking what this would undo…</p>}
        {preview && !preview.can_void && (
          <p className="text-danger" data-testid="void-blocked">{preview.blocked_reason}</p>
        )}
        {preview?.can_void && (
          <div data-testid="void-preview" className="space-y-2">
            <p>
              It takes off <strong>{preview.price_points}</strong> price{preview.price_points === 1 ? '' : 's'} this
              invoice set{preview.stock.length ? ' and takes this stock back:' : '. It added no stock.'}
            </p>
            {preview.stock.length > 0 && (
              <ul className="list-disc pl-5" data-testid="void-stock">
                {preview.stock.map((s) => (
                  <li key={s.item_id} data-testid={`void-stock-${s.item_id}`}>{s.item_name}: {s.qty} {s.uom}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <label className="block">
          <span className="block text-xs uppercase tracking-overline text-ink-muted mb-1">Why is it being voided?</span>
          <textarea value={reason} maxLength={500} rows={3} data-testid="void-reason" onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-ink" />
        </label>
        {error && <p className="text-danger" data-testid="void-error">{error}</p>}
      </ConfirmDialog>

      <StepUpDialog open={step === 'confirm'} action="void_invoice" testid="void-step-up"
        title="Confirm the void"
        summary={`Void invoice ${invoice.invoice_no || ''}: its prices come off and its stock is taken back.`}
        perform={(token) => voidInvoice(invoice.id, { command_id: command.current, reason: reason.trim() }, token)}
        onCancel={() => setStep('review')} onDone={(result) => { close(); onVoided?.(result); }} />
    </section>
  );
}
